import { useCallback, useEffect, useState } from 'react';
import { cmsGraphql } from '@xolv-mybrightlink/common';
import { useFetchSavedEventSlugs } from './useFetchSavedEventSlugs';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import { gql } from '@apollo/client';
import _ from 'lodash';
import { CmsEvent, EventResponse } from '../models/EventResult';

const GET_EVENT_LIST_BY_SLUGS = gql`
  query Events($slugs: [String]) {
    events(
      where: {
        nameIn: $slugs
        orderby: { field: EVENT_START_DATE, order: ASC }
      }
    ) {
      nodes {
        databaseId
        title(format: RENDERED)
        slug
        eventsCategories {
          nodes {
            name
            databaseId
            slug
          }
        }
        venue {
          address
          title(format: RENDERED)
        }
        featuredImage {
          node {
            mediaDetails {
              sizes {
                sourceUrl
                name
              }
            }
          }
        }
        startDate
        endDate
        allDay
        cost
      }
    }
  }
`;

type SavedEventFetchResult =
  | { type: 'LOADING' }
  | { type: 'ERROR' }
  | { type: 'DATA'; events: CmsEvent[]; hasLoadMore: boolean };

export const useFetchSavedEvents = (
  anchorGuid: string | null,
  pageSize?: number,
  retry?: boolean,
) => {
  const [savedEventFetchResult, setSavedEventFetchResult] =
    useState<SavedEventFetchResult>({ type: 'LOADING' });
  const getSavedSlugs = useFetchSavedEventSlugs();
  const intl = useIntl();

  const getEventListBySlugs = useCallback(
    async (slugs: string[]) => {
      try {
        const result = await cmsGraphql.query<EventResponse>({
          query: GET_EVENT_LIST_BY_SLUGS,
          variables: { slugs },
        });
        const cloneEvents = _.cloneDeep(result.data.events.nodes);
        cloneEvents.forEach((item) => (item.isSaved = true));
        return cloneEvents;
      } catch {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
      }
    },
    [intl],
  );

  useEffect(() => {
    const fetch = async () => {
      try {
        setSavedEventFetchResult({ type: 'LOADING' });
        const savedSlugs = await getSavedSlugs(anchorGuid, pageSize ?? 10);
        if (savedSlugs.bookmarks.length === 0) {
          setSavedEventFetchResult({
            type: 'DATA',
            events: [],
            hasLoadMore: false,
          });
          return;
        }
        const savedEvents = await getEventListBySlugs(
          savedSlugs.bookmarks.map((bookmark) => bookmark.slug),
        );
        setSavedEventFetchResult({
          type: 'DATA',
          events: savedEvents ? savedEvents : [],
          hasLoadMore: savedEvents ? savedSlugs.hasLoadMore : false,
        });
      } catch (e) {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
        setSavedEventFetchResult({ type: 'ERROR' });
      }
    };
    fetch();
  }, [anchorGuid, getEventListBySlugs, getSavedSlugs, intl, pageSize, retry]);

  return savedEventFetchResult;
};
